import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  shortlistsControllerAddPlayerV1,
  shortlistsControllerCreateShortlistV1,
  shortlistsControllerListShortlistsV1,
  shortlistsControllerRemovePlayerV1,
  shortlistsControllerRemoveShortlistV1,
  shortlistsControllerUpdateShortlistV1,
} from '@/api/generated/shortlists/shortlists';
import type {
  AddShortlistPlayerDto,
  CreateShortlistDto,
  UpdateShortlistDto,
} from '@/api/generated/model';
import { playerKeys } from './usePlayers';

export const shortlistKeys = {
  all: ['shortlists'] as const,
  lists: () => [...shortlistKeys.all, 'list'] as const,
  list: () => [...shortlistKeys.lists(), 'default'] as const,
};

/**
 * Every shortlist the user can see — theirs plus anything shared.
 *
 * Unpaginated in practice: the picker is a dropdown, and an agency with more
 * than a hundred shortlists has a different problem than pagination. `limit`
 * is set to the backend's maximum rather than left to default to 20, so the
 * dropdown does not quietly omit older lists.
 */
export function useShortlistsQuery() {
  return useQuery({
    queryKey: shortlistKeys.list(),
    queryFn: ({ signal }) => shortlistsControllerListShortlistsV1({ limit: 100 }, { signal }),
  });
}

export function useCreateShortlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateShortlistDto) => shortlistsControllerCreateShortlistV1(dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shortlistKeys.lists() });
    },
  });
}

export function useUpdateShortlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: UpdateShortlistDto }) =>
      shortlistsControllerUpdateShortlistV1(id, dto),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: shortlistKeys.lists() });
    },
  });
}

export function useDeleteShortlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => shortlistsControllerRemoveShortlistV1(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: shortlistKeys.lists() }),
        // A player list filtered by the deleted shortlist is now meaningless.
        queryClient.invalidateQueries({ queryKey: playerKeys.lists() }),
      ]);
    },
  });
}

export function useAddToShortlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: AddShortlistPlayerDto }) =>
      shortlistsControllerAddPlayerV1(id, dto),
    onSuccess: async () => {
      await Promise.all([
        // The count on the shortlist changed...
        queryClient.invalidateQueries({ queryKey: shortlistKeys.lists() }),
        // ...and so did any list filtered by `shortlistId`.
        queryClient.invalidateQueries({ queryKey: playerKeys.lists() }),
      ]);
    },
  });
}

export function useRemoveFromShortlistMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, playerId }: { id: string; playerId: string }) =>
      shortlistsControllerRemovePlayerV1(id, playerId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: shortlistKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: playerKeys.lists() }),
      ]);
    },
  });
}
